// amount - string or number | type - if formatted with +,currency and .xx | currency- dollars,euros etc | withSign- add + or -
// formatted example: with sign: -$100.12. without: $100.12 returned as a string
// unformatted example: 100 and returned as a number
import { FORMAT_TYPE, MAX_VALUE_BY_CUSTOMER } from '../constants/general';

const formatAmount = (amount: string | number, type: string, currency: string, withSign: boolean, withDots = true): any => {
  amount = amount.toString();
  const isDots = amount.split('.');
  const sign = +amount.replace('$', '') >= 0 ? '+' : '-';
  amount = isDots ? isDots[0] : amount;

  const isolateNumber = (type === 'formatted')
    ? amount.toString().replace('-', '').replace('$', '')
    : amount.toString().replace(currency, '').toLocaleString();

  if (type === 'formatted') {
    return `${withSign ? sign : ''}${currency}${(+isolateNumber).toLocaleString()}${withDots && isDots.length > 1 ? `.${isDots[1]}` : ''}`;
  }
  const unformattedAmount = isolateNumber.split(' ').join('').replace('+', '').replace(',', '');
  return (+unformattedAmount);
};

const checkAmountSign = (amount: string) => {
  const newAmount = amount.replace(/\s/g, '')
    .replace(',', '')
    .replace('+', '')
    .replace('$', '');
  return Math.sign(+newAmount);
};

const handleMoneyInputOnChange = (event: { target: { value: string; }; }, setInputValue: (s: string) => void, autoSaving: boolean) => {
  if (setInputValue && typeof setInputValue === 'function') {
    if (event.target.value.length <= 2) {
      setInputValue('$ ');
      return;
    }
    const value = event.target.value.substring(2).replaceAll(',', '');
    const dotIndex = value.indexOf('.');
    if (dotIndex !== value.lastIndexOf('.')) return;
    if (dotIndex !== -1 && ((value.length - dotIndex) > 3)) return;
    if (Number.isNaN(parseFloat(value))) return;
    if (`${parseFloat(value)}`.length !== value.length && (dotIndex === -1 || value.length - dotIndex >= 2)) return;
    const formattedValue = formatAmount(value, 'formatted', '$', false, !autoSaving).trim().replace('$', '$ ');
    if (!formattedValue.includes('NaN')) setInputValue(formattedValue);
  }
};

const addDotsWithNullIfNeeded = (value:string) => {
  if (value.indexOf('.') !== -1 && (value.length - value.indexOf('.') === 2)) return `${value}0`;
  if (value.indexOf('.') === -1) return `${value}.00`;
  return value;
};

const handleInputWithCurrencyOnChange = (event: { target: { value: string; }; }, setInputValue: (s: string) => void, onlyIntegerValue?: boolean) => {
  if (setInputValue && typeof setInputValue === 'function') {
    const value = event.target.value.replaceAll(',', '');
    if (value.length === 0) {
      setInputValue(value);
      return;
    }
    const dotIndex = value.indexOf('.');
    if (dotIndex !== value.lastIndexOf('.')) return;
    if (dotIndex !== -1 && onlyIntegerValue) return;
    if (dotIndex !== -1 && ((value.length - dotIndex) > 3)) return;
    if (value.length > 0 && Number.isNaN(parseFloat(value))) return;
    if (parseFloat(value) > MAX_VALUE_BY_CUSTOMER) return;
    if (`${parseFloat(value)}`.length !== value.length && (dotIndex === -1 || value.length - dotIndex >= 2)) return;
    const formattedValue = dotIndex === -1 ? parseFloat(value).toLocaleString() : value.length - dotIndex === 1 ? `${parseFloat(value).toLocaleString()}.` : parseFloat(value).toLocaleString();
    setInputValue(formattedValue);
  }
};

const convertIntToFloatStr = (number:number) => {
  if (number || number === 0) {
    const addDots = Number.isInteger(number);
    return `${number}${addDots ? '.00' : `${number}`.length - `${number}`.indexOf('.') === 2 ? '0' : ''}`;
  }
  return '';
};
const currencyInputValueToNumber = (value:string) => {
  const numberValue = parseFloat(value.replaceAll(',', ''));
  return Number.isNaN(numberValue) ? 0 : numberValue;
};

const CURRENCIES_LIST: {[key: string]: string} = {
  AED: 'د.إ',
  AFN: '؋',
  ALL: 'L',
  AMD: '֏',
  ANG: 'ƒ',
  AOA: 'Kz',
  ARS: '$',
  AUD: '$',
  AWG: 'ƒ',
  AZN: '₼',
  BAM: 'KM',
  BBD: '$',
  BDT: '৳',
  BGN: 'лв',
  BHD: '.د.ب',
  BIF: 'FBu',
  BMD: '$',
  BND: '$',
  BOB: '$b',
  BOV: 'BOV',
  BRL: 'R$',
  BSD: '$',
  BTC: '₿',
  BTN: 'Nu.',
  BWP: 'P',
  BYN: 'Br',
  BYR: 'Br',
  BZD: 'BZ$',
  CAD: '$',
  CDF: 'FC',
  CHE: 'CHE',
  CHF: 'CHF',
  CHW: 'CHW',
  CLF: 'CLF',
  CLP: '$',
  CNH: '¥',
  CNY: '¥',
  COP: '$',
  COU: 'COU',
  CRC: '₡',
  CUC: '$',
  CUP: '₱',
  CVE: '$',
  CZK: 'Kč',
  DJF: 'Fdj',
  DKK: 'kr',
  DOP: 'RD$',
  DZD: 'دج',
  EEK: 'kr',
  EGP: '£',
  ERN: 'Nfk',
  ETB: 'Br',
  ETH: 'Ξ',
  EUR: '€',
  FJD: '$',
  FKP: '£',
  GBP: '£',
  GEL: '₾',
  GGP: '£',
  GHC: '₵',
  GHS: 'GH₵',
  GIP: '£',
  GMD: 'D',
  GNF: 'FG',
  GTQ: 'Q',
  GYD: '$',
  HKD: '$',
  HNL: 'L',
  HRK: 'kn',
  HTG: 'G',
  HUF: 'Ft',
  IDR: 'Rp',
  ILS: '₪',
  IMP: '£',
  INR: '₹',
  IQD: 'ع.د',
  IRR: '﷼',
  ISK: 'kr',
  JEP: '£',
  JMD: 'J$',
  JOD: 'JD',
  JPY: '¥',
  KES: 'KSh',
  KGS: 'лв',
  KHR: '៛',
  KMF: 'CF',
  KPW: '₩',
  KRW: '₩',
  KWD: 'KD',
  KYD: '$',
  KZT: '₸',
  LAK: '₭',
  LBP: '£',
  LKR: '₨',
  LRD: '$',
  LSL: 'M',
  LTC: 'Ł',
  LTL: 'Lt',
  LVL: 'Ls',
  LYD: 'LD',
  MAD: 'MAD',
  MDL: 'lei',
  MGA: 'Ar',
  MKD: 'ден',
  MMK: 'K',
  MNT: '₮',
  MOP: 'MOP$',
  MRO: 'UM',
  MRU: 'UM',
  MUR: '₨',
  MVR: 'Rf',
  MWK: 'MK',
  MXN: '$',
  MXV: 'MXV',
  MYR: 'RM',
  MZN: 'MT',
  NAD: '$',
  NGN: '₦',
  NIO: 'C$',
  NOK: 'kr',
  NPR: '₨',
  NZD: '$',
  OMR: '﷼',
  PAB: 'B/.',
  PEN: 'S/.',
  PGK: 'K',
  PHP: '₱',
  PKR: '₨',
  PLN: 'zł',
  PYG: 'Gs',
  QAR: '﷼',
  RMB: '￥',
  RON: 'lei',
  RSD: 'Дин.',
  RUB: '₽',
  RWF: 'R₣',
  SAR: '﷼',
  SBD: '$',
  SCR: '₨',
  SDG: 'ج.س.',
  SEK: 'kr',
  SGD: 'S$',
  SHP: '£',
  SLL: 'Le',
  SOS: 'S',
  SRD: '$',
  SSP: '£',
  STD: 'Db',
  STN: 'Db',
  SVC: '$',
  SYP: '£',
  SZL: 'E',
  THB: '฿',
  TJS: 'SM',
  TMT: 'T',
  TND: 'د.ت',
  TOP: 'T$',
  TRL: '₤',
  TRY: '₺',
  TTD: 'TT$',
  TVD: '$',
  TWD: 'NT$',
  TZS: 'TSh',
  UAH: '₴',
  UGX: 'USh',
  USD: '$',
  UYI: 'UYI',
  UYU: '$U',
  UYW: 'UYW',
  UZS: 'лв',
  VEF: 'Bs',
  VES: 'Bs.S',
  VND: '₫',
  VUV: 'VT',
  WST: 'WS$',
  XAF: 'FCFA',
  XBT: 'Ƀ',
  XCD: '$',
  XOF: 'CFA',
  XPF: '₣',
  XSU: 'Sucre',
  XUA: 'XUA',
  YER: '﷼',
  ZAR: 'R',
  ZMW: 'ZK',
  ZWD: 'Z$',
  ZWL: '$',
};

export const getLocalAmount = (localAmount: number, localCurrencyCode: string) => {
  const amount: string = localAmount.toString();
  return `${formatAmount(addDotsWithNullIfNeeded(amount), FORMAT_TYPE.FORMATTED, CURRENCIES_LIST[localCurrencyCode], true, true)}`;
};

export const isParseAble = (value: string) => {
  try {
    const intValue = JSON.parse(value);
    return !Number.isNaN(intValue);
  } catch (e) {
    return false;
  }
};

export const formatValueWithoutCurrency = (value: number, maxFraction: number) => new Intl
  .NumberFormat('en-US', {
    minimumFractionDigits: maxFraction,
    maximumFractionDigits: maxFraction,
  }).format(value);

export const convertValueToUSD = (value: number, maxFraction: number) => new Intl
  .NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: maxFraction,
  })
  .format(value);

export const convertValueToUSDv2 = (value: number, ignoreFriction?: boolean) => value.toLocaleString('en-US', { maximumFractionDigits: ignoreFriction ? 0 : 2 });

export const convertValueToPercent = (value: number) => new Intl.NumberFormat('en-US', { style: 'percent' }).format(value);

export const convertToTimeFormat = (num: number) => {
  const hours = Math.floor(num / 60);
  const minutes = num % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const cleanTransferValue = (value: string) => value.replace('$', '')
  .replaceAll(',', '')
  .replaceAll('.', '');

export {
  formatAmount, checkAmountSign, handleMoneyInputOnChange, convertIntToFloatStr, addDotsWithNullIfNeeded, handleInputWithCurrencyOnChange, currencyInputValueToNumber,
};
